import type { ReactNode } from 'react';
import { Badge } from '../ui/badge';
import { cn } from '../lib/utils';
import {
    unitTopicBadgeClass,
    unitTopicsFieldsetClass,
    unitTopicsLegendClass,
} from './unitCardTheme';
import type { LearningObjectiveDto } from './InProgressUnitsPanel/types';
import { UnitHeaderTopicsWithObjectives } from './UnitHeaderTopicsWithObjectives';

export type UnitHeaderTone = 'default' | 'chalkboard';

/** Plain "Topics" label shown before topic chips on unit card headers. */
export function UnitHeaderTopicsPlainLabel() {
    return <span className="shrink-0 text-xs text-gray-600">Topics </span>;
}

interface UnitHeaderTopicLabelsProps {
    topics: string[] | undefined;
    tone?: UnitHeaderTone;
    /** When false, renders topic chips only (no "Topics" fieldset label). */
    showLabel?: boolean;
}

/** Topic names as solid green chips in a light "Topics" fieldset. */
export function UnitHeaderTopicLabels({
    topics,
    tone: _tone = 'default',
    showLabel = true,
}: UnitHeaderTopicLabelsProps) {
    const filtered = (topics ?? []).map((t) => String(t).trim()).filter(Boolean);

    const labels =
        filtered.length === 0 ? (
            <Badge variant="outline" className={cn(unitTopicBadgeClass, 'opacity-80')}>
                Untitled Unit
            </Badge>
        ) : (
            <span className="inline-flex max-w-full flex-wrap items-center gap-1.5">
                {filtered.map((t, i) => (
                    <Badge key={`${t}-${i}`} variant="outline" className={unitTopicBadgeClass}>
                        {t}
                    </Badge>
                ))}
            </span>
        );

    if (!showLabel) {
        return (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-800">
                <UnitHeaderTopicsPlainLabel />
                {labels}
            </div>
        );
    }

    return (
        <fieldset className={unitTopicsFieldsetClass}>
            <legend className="sr-only">Topics</legend>
            <div aria-hidden className={unitTopicsLegendClass}>
                Topics
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-800 [&_a]:text-blumine [&_a]:underline">
                {labels}
            </div>
        </fieldset>
    );
}

/** Bold subject line only (pair with `UnitHeaderTopicLabels` on a separate row). */
export function UnitHeaderSubjectLine({
    subjects,
    tone: _tone = 'default',
    trailing,
}: {
    subjects: string[] | undefined;
    tone?: UnitHeaderTone;
    /** Rendered after the subject text (e.g. "(Assessment Only)"). */
    trailing?: ReactNode;
}) {
    const subjectList = (subjects ?? []).map((s) => String(s).trim()).filter(Boolean);
    const subjectLine = subjectList.length > 0 ? subjectList.join(', ') : null;
    if (!subjectLine && !trailing) return null;
    return (
        <span className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 text-xl font-bold leading-snug text-gray-900">
            {subjectLine ? <span>{subjectLine}</span> : null}
            {trailing}
        </span>
    );
}

interface UnitHeaderSubjectAndTopicsProps {
    subjects: string[] | undefined;
    topics: string[] | undefined;
    /** e.g. "(Assessment Only)" after the subject line */
    trailing?: ReactNode;
    tone?: UnitHeaderTone;
    /** When false, topic chips render without the "Topics" fieldset label. */
    showTopicsLabel?: boolean;
    /** When set, topic chips expand learning objectives inline (In Progress / Completed). */
    learningObjectives?: LearningObjectiveDto[];
    /** Show completion fractions on expandable topic chips. */
    showObjectiveStatus?: boolean;
}

/** Bold subject line, then topic labels (optionally with fold-in learning objectives). */
export function UnitHeaderSubjectAndTopics({
    subjects,
    topics,
    trailing,
    tone = 'default',
    showTopicsLabel = true,
    learningObjectives,
    showObjectiveStatus = false,
}: UnitHeaderSubjectAndTopicsProps) {
    const topicsNode =
        learningObjectives && learningObjectives.length > 0 ? (
            <UnitHeaderTopicsWithObjectives
                topics={topics}
                learningObjectives={learningObjectives}
                showStatus={showObjectiveStatus}
                tone={tone}
            />
        ) : (
            <UnitHeaderTopicLabels topics={topics} tone={tone} showLabel={showTopicsLabel} />
        );

    return (
        <div className="flex flex-col gap-1.5">
            <UnitHeaderSubjectLine subjects={subjects} tone={tone} trailing={trailing} />
            {topicsNode}
        </div>
    );
}
